define('home/mixins/withLoginOverlay',[
    'jquery',
    'underscore',
    'backbone-marionette',

    'home/models/Session',
    'home/views/LoginOverlayView',
    'home/templates/signUpAsideModule',
    'home/utils/layoutUtils',
], function (
    $,
    _,
    Marionette,

    Session,
    LoginOverlayView,
    signUpAsideModuleTemplate,
    layoutUtils
) {
    'use strict';

    /**
     * Adds login overlay support to the view to which the mixin is applied.
     *
     * If View's template has defined a region for it, renders the log in module
     * in the region which has the ability to expand the login overlay.
     *
     * Adds a login footer which shows if the signUpAsideModule is not in
     * the viewport.
     *
     * Handles all the click events for the footer and signUpAsideModule
     * to show the overlay when appropriate.
     *
     * Overlay itself is a view which handles authentication.
     *
     *
     * Expects to be applied to a Marionette View using the withSubviews mixin,
     * or a Marionette Layout.
     * Can be passed a prompt, which is the message shown to the user above the
     * log in options. If not given, will use a default message.
     */
    var LoginOverlayMixin = {
        regions: {
            loginModuleRegion: '[data-role=sign-up-module]',
        },

        events: {
            'click [data-action~=show-login-overlay]': 'showLoginOverlay',
        },

        initialize: function (_initialize, options) {
            _initialize.call(this, options);
            this.boundEnsureLoginPromptInViewport = _.bind(this.ensureLoginPromptInViewport, this);
            $(window).scroll(this.boundEnsureLoginPromptInViewport);
        },

        onClose: function () {
            this.unbindScrollHandler();
            if (this.loginOverlayView)
                this.loginOverlayView.close();
        },

        onRender: function (_onRender) {
            if (_.isFunction(_onRender))
                _onRender.call(this);

            var createLoginModuleView = function () {
                return new Marionette.ItemView({
                    template: signUpAsideModuleTemplate,
                });
            };

            // Allow mixin to work with either Marionette Layouts or with a View
            // with the withSubviews mixin.
            if (this.activateRegion)
                this.activateRegion(this.loginModuleRegion, createLoginModuleView);
            else
                this.loginModuleRegion.show(createLoginModuleView());
        },

        /**
         * Ensures there is a login prompt in the viewport. Shows the footer
         * prompt iff the module is not in the viewport.
         */
        ensureLoginPromptInViewport: _.debounce(function () {
            var loginPromptModule = this.$('[data-role=login-prompt-module]');
            var showFooter = !loginPromptModule.length || !layoutUtils.isElementInViewport(loginPromptModule);
            this.showLoginFooter(showFooter);
        }, 200),

        unbindScrollHandler: function () {
            $(window).off('scroll', this.boundEnsureLoginPromptInViewport);
        },

        showLoginOverlay: function () {
            this.loginOverlayView.showAsOverlay();
        },

        showLoginFooter: function (shouldShow) {
            this.loginOverlayView.showAsFooter(shouldShow);
        },

        onDomRefresh: function (overlayViewOptions, _onDomRefresh) {
            if (_.isFunction(_onDomRefresh))
                _onDomRefresh.call(this);

            // Since the LoginOverlayView is inserted after this view's element,
            // don't need to readd/rerender it when this view is rerendered, as
            // it won't be removed. One initialization is enough.
            if (this.loginOverlayView)
                return;

            // Add the overlay view to the DOM. It'll be hidden by default
            // and shown later by scroll or click events.
            this.loginOverlayView = new LoginOverlayView(overlayViewOptions);
            this.listenTo(this.loginOverlayView, 'shown:overlay', this.unbindScrollHandler);
            this.$el.after(this.loginOverlayView.render().$el);
        },
    };

    /**
     * @param  {string} prompt - (optional) The message shown to the user above the log in prompt.
     */
    return function (prompt) {
        // Mixin only applies on logged out pages
        if (!Session.isKnownToBeLoggedOut())
            return;

        this.regions = _.extend({}, LoginOverlayMixin.regions, this.regions);

        this.events = _.extend({}, LoginOverlayMixin.events, this.events);

        this.initialize = _.wrap(this.initialize, LoginOverlayMixin.initialize);

        this.onRender = _.wrap(this.onRender, LoginOverlayMixin.onRender);

        this.onDomRefresh = _.wrap(this.onDomRefresh, _.partial(LoginOverlayMixin.onDomRefresh, { prompt: prompt }));

        _.extend(this, _.pick(LoginOverlayMixin, [
            'showLoginFooter',
            'showLoginOverlay',
            'ensureLoginPromptInViewport',
            'unbindScrollHandler',
        ]));
    };
});

